import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, X, Sparkles, ArrowRight, Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { TEST_PARAMETERS, COMPARISON_CATEGORIES, getCategoryParams } from "@/lib/testParameters";
import { useLivePricing } from "@/lib/useLivePricing";

// Structural config only — actual prices come from the SSoT pricing
// hook below. This section previously hardcoded a 20%-off "online
// prepaid" tier that no longer exists under the All-Inclusive rollout.
const COLS = [
  { id: "basic" },
  { id: "tank", featured: true },
  { id: "bore" },
];

const OPEN_DELAY = 80;
const CLOSE_DELAY = 250;

/**
 * Interactive tick that reveals the category's exact parameters for that test.
 * Stable behaviour:
 *  - Hover with delays (80ms open, 250ms close) so the user can comfortably
 *    move the mouse from the tick into the popover.
 *  - Click toggles a "pinned" state that ignores hover-out — popover stays
 *    open until user clicks outside, presses Esc, or clicks another icon.
 *  - On touch devices (no hover), the click handler is the primary interaction.
 */
function ParameterTick({ category, testName, items, colId, pinnedId, setPinnedId }) {
  const [hoverOpen, setHoverOpen] = useState(false);
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const myKey = `${colId}:${category}`;
  const isPinned = pinnedId === myKey;
  const open = isPinned || hoverOpen;

  const clearTimers = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
  };

  useEffect(() => () => clearTimers(), []);

  const onEnter = () => {
    clearTimers();
    openTimer.current = setTimeout(() => setHoverOpen(true), OPEN_DELAY);
  };
  const onLeave = () => {
    clearTimers();
    closeTimer.current = setTimeout(() => setHoverOpen(false), CLOSE_DELAY);
  };
  const onTriggerClick = () => {
    clearTimers();
    if (isPinned) {
      setPinnedId(null);
      setHoverOpen(false);
    } else {
      setPinnedId(myKey);
      setHoverOpen(true);
    }
  };

  // When pinned externally toggled off, also clear local hover state
  useEffect(() => {
    if (!isPinned && !hoverOpen) clearTimers();
  }, [isPinned, hoverOpen]);

  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) { setHoverOpen(false); if (isPinned) setPinnedId(null); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`compare-${colId}-${category.toLowerCase().replace(/[^a-z]+/g, "-")}-tick`}
          aria-label={`Show ${category} parameters for ${testName}`}
          aria-expanded={open}
          onMouseEnter={onEnter}
          onMouseLeave={onLeave}
          onFocus={() => { clearTimers(); setHoverOpen(true); }}
          onBlur={() => { if (!isPinned) onLeave(); }}
          onClick={onTriggerClick}
          className={`group relative inline-flex w-9 h-9 rounded-full bg-cyan-500 text-white items-center justify-center ring-2 ${
            isPinned ? "ring-cyan-600 ring-offset-1" : "ring-cyan-200 hover:ring-cyan-400"
          } focus:outline-none focus-visible:ring-cyan-500 cursor-pointer transition-shadow`}
        >
          <Check className="w-4 h-4" strokeWidth={3} />
          <span
            aria-hidden
            className="absolute -bottom-1 -right-1 inline-flex w-3.5 h-3.5 rounded-full bg-white text-cyan-600 items-center justify-center shadow-sm border border-cyan-200"
          >
            <Info className="w-2.5 h-2.5" strokeWidth={3} />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={10}
        className="w-72 max-w-[90vw] text-left"
        onMouseEnter={() => { clearTimers(); setHoverOpen(true); }}
        onMouseLeave={() => { if (!isPinned) onLeave(); }}
        onOpenAutoFocus={(e) => e.preventDefault()}
        data-testid={`compare-${colId}-${category.toLowerCase().replace(/[^a-z]+/g, "-")}-popover`}
      >
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-cyan-700">{category}</div>
        <h4 className="font-display mt-1 text-sm font-bold text-slate-900 leading-snug">{testName}</h4>
        <ul className="mt-2.5 space-y-1 text-xs text-slate-700 leading-relaxed">
          {items.map((p) => (
            <li key={p} className="flex gap-1.5">
              <Check className="w-3 h-3 mt-0.5 text-cyan-500 flex-shrink-0" strokeWidth={3} />
              <span>{p}</span>
            </li>
          ))}
        </ul>
        {isPinned && (
          <p className="mt-2.5 text-[10px] text-slate-400 italic">Click anywhere to close</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

function ExcludedTick() {
  return (
    <span className="inline-flex w-9 h-9 rounded-full bg-slate-100 text-slate-300 items-center justify-center" aria-label="Not included">
      <X className="w-4 h-4" />
    </span>
  );
}

export function TestComparison() {
  // One "pinned" popover open at a time — clicking a new tick swaps it in.
  const [pinnedId, setPinnedId] = useState(null);
  // Feb 2026 SSoT — live all-inclusive prices from `/api/pricing/tests`
  // via the shared hook. Falls back to the bundled TESTS array so the
  // page still renders when the endpoint is momentarily unavailable.
  const liveTests = useLivePricing();
  const priceById = Object.fromEntries((liveTests || []).map((t) => [t.id, t.retail]));
  // Esc to dismiss
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setPinnedId(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <section id="compare" className="py-12 lg:py-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-600">Compare tests</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 leading-[1.05]">
            Which parameters does each test cover?
          </h2>
          <p className="mt-4 text-base text-slate-600">At-a-glance comparison of what's included in every Aware Water Testing package. Hover or click any tick for the exact parameters tested.</p>
        </div>

        <div data-testid="test-comparison-table" className="rounded-3xl bg-white border border-slate-200 overflow-visible shadow-sm">
          <div className="overflow-x-auto overflow-y-visible">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="text-left p-5 text-[11px] uppercase tracking-wider font-bold text-cyan-400 min-w-[200px]">Parameter Category</th>
                  {COLS.map((c) => {
                    const t = TEST_PARAMETERS[c.id];
                    return (
                      <th key={c.id} className={`text-center p-5 ${c.featured ? "bg-cyan-500" : ""}`}>
                        <div className="flex flex-col items-center gap-1.5">
                          {c.featured && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-[9px] font-bold uppercase tracking-wider">
                              <Sparkles className="w-2.5 h-2.5" /> Most Popular
                            </span>
                          )}
                          <div className="font-display font-semibold text-sm text-white leading-tight">{t.name}</div>
                          <div className="flex items-baseline gap-1.5" data-testid={`compare-price-${c.id}`}>
                            <span className="text-lg font-bold text-white">
                              ${priceById[c.id] != null ? Number(priceById[c.id]).toFixed(0) : "—"}
                            </span>
                            <span className="text-[10px] font-medium uppercase tracking-wider text-white/70">
                              All-inclusive
                            </span>
                          </div>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {COMPARISON_CATEGORIES.map((category, idx) => (
                  <tr key={category} className={idx % 2 ? "bg-slate-50/40" : "bg-white"}>
                    <td className="p-4 text-sm font-semibold text-slate-900">{category}</td>
                    {COLS.map((c) => {
                      const items = getCategoryParams(c.id, category);
                      const t = TEST_PARAMETERS[c.id];
                      return (
                        <td key={c.id} className={`p-4 text-center ${c.featured ? "bg-cyan-50/40" : ""}`}>
                          {items ? (
                            <ParameterTick
                              category={category}
                              testName={t.name}
                              items={items}
                              colId={c.id}
                              pinnedId={pinnedId}
                              setPinnedId={setPinnedId}
                            />
                          ) : (
                            <ExcludedTick />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr className="bg-white">
                  <td className="p-4 text-sm font-semibold text-slate-900">Plain-English summary report</td>
                  {COLS.map((c) => (
                    <td key={c.id} className={`p-4 text-center ${c.featured ? "bg-cyan-50/40" : ""}`}>
                      <span className="inline-flex w-9 h-9 rounded-full bg-cyan-500 text-white items-center justify-center">
                        <Check className="w-4 h-4" strokeWidth={3} />
                      </span>
                    </td>
                  ))}
                </tr>
                <tr className="bg-slate-50 border-t border-slate-200">
                  <td className="p-4"></td>
                  {COLS.map((c) => (
                    <td key={c.id} className={`p-4 text-center ${c.featured ? "bg-cyan-50/40" : ""}`}>
                      <Link
                        to={`/book-water-test?test=${c.id}`}
                        data-testid={`compare-book-${c.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-semibold ${
                          c.featured ? "btn-aqua" : "bg-slate-900 text-white hover:bg-slate-800"
                        } transition-colors`}
                      >
                        Book <ArrowRight className="w-3 h-3" />
                      </Link>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <p className="mt-5 text-xs text-slate-500 text-center">
          Hover (or tap) the <span className="inline-flex items-center"><Info className="w-3 h-3 inline mx-0.5 text-cyan-600" /></span> tick to see the exact parameters tested. Click to pin it open.
        </p>
      </div>
    </section>
  );
}
